<template>
  <div class="survey-manage">
    <Card>
      <div class="toolbar">
        <div class="toolbar-left">
          <Button type="primary" icon="md-add" @click="handleCreate">新建问卷</Button>
          <Button icon="md-refresh" class="ml-8" @click="handleSearch">刷新</Button>
        </div>
        <div class="toolbar-right">
          <Input v-model="query.key" placeholder="搜索标题" style="width: 200px" @on-enter="handleSearch" />
          <Select v-model="query.type" style="width: 140px" clearable placeholder="类型" @on-change="handleSearch">
            <Option :value="1">问卷调查</Option>
            <Option :value="2">用工调查</Option>
          </Select>
          <Select v-model="query.status" style="width: 140px" clearable placeholder="状态" @on-change="handleSearch">
            <Option :value="0">草稿</Option>
            <Option :value="1">已发布</Option>
            <Option :value="2">已结束</Option>
          </Select>
        </div>
      </div>

      <Table :columns="columns" :data="list" :loading="loading" row-key="id" @on-selection-change="selectionChange">
        <template slot-scope="{ row }" slot="type">
          <Tag :color="row.type === 1 ? 'blue' : 'purple'">{{ row.type === 1 ? '问卷调查' : '用工调查' }}</Tag>
        </template>
        <template slot-scope="{ row }" slot="status">
          <Tag :color="statusColor(row.status)">{{ statusText(row.status) }}</Tag>
        </template>
        <template slot-scope="{ row }" slot="action">
          <Button type="text" size="small" @click="handleEdit(row)">编辑</Button>
          <Button v-if="row.status !== 1" type="text" size="small" @click="handlePublish(row)">发布</Button>
          <Button v-if="row.type === 2" type="text" size="small" @click="handleViewJob(row)">用工汇总</Button>
          <Button type="text" size="small" style="color:#ed4014" @click="handleDelete(row)">删除</Button>
        </template>
      </Table>

      <div class="pager">
        <Page :total="total" :current="query.page" :page-size="query.limit" show-total
          @on-change="changePage" @on-page-size-change="changeSize" show-sizer />
      </div>
    </Card>

    <!-- 新建/编辑问卷 Modal -->
    <Modal v-model="modalVisible" :title="isEdit ? '编辑问卷' : '新建问卷'" width="720"
      :mask-closable="false" :loading="saving">
      <Form ref="surveyForm" :model="form" :rules="rules" :label-width="90">
        <FormItem label="问卷类型">
          <RadioGroup v-model="form.type" @on-change="onTypeChange">
            <Radio :label="1">问卷调查</Radio>
            <Radio :label="2">用工调查</Radio>
          </RadioGroup>
        </FormItem>
        <FormItem label="标题" prop="title">
          <Input v-model="form.title" placeholder="请输入标题/说明" />
        </FormItem>
        <FormItem label="发布对象" v-if="form.type === 2">
          <Select v-model="form.target_scope" style="width: 200px">
            <Option value="all">全部企业</Option>
            <Option value="company">已审核企业</Option>
          </Select>
        </FormItem>
        <FormItem label="有效期">
          <DatePicker type="datetimerange" v-model="form.validRange" format="yyyy-MM-dd HH:mm:ss"
            placeholder="请选择有效期" style="width: 100%"></DatePicker>
        </FormItem>

        <!-- 问卷调查：题目配置 -->
        <Divider v-if="form.type === 1">题目设置</Divider>
        <div v-if="form.type === 1" class="question-builder">
          <div class="question-item" v-for="(q, qi) in form.config" :key="qi">
            <div class="q-head">
              <span class="q-no">Q{{ qi + 1 }}</span>
              <Input v-model="q.title" placeholder="题目内容" style="flex:1; margin: 0 12px" />
              <Button type="text" size="small" style="color:#ed4014" @click="removeQuestion(qi)">
                <Icon type="md-trash" />
              </Button>
            </div>
            <div class="q-opts">
              <RadioGroup v-model="q.qtype" size="small">
                <Radio label="radio">单选</Radio>
                <Radio label="checkbox">多选</Radio>
                <Radio label="input">填空</Radio>
              </RadioGroup>
              <div v-if="q.qtype !== 'input'" class="opt-list">
                <div class="opt-row" v-for="(op, oi) in q.options" :key="oi">
                  <Input v-model="q.options[oi]" placeholder="选项" style="width: 240px" />
                  <Button type="text" size="small" @click="removeOption(q, oi)">删</Button>
                </div>
                <Button size="small" icon="md-add" @click="addOption(q)">添加选项</Button>
              </div>
            </div>
          </div>
          <Button type="dashed" long icon="md-add" @click="addQuestion">添加题目</Button>
        </div>
      </Form>
      <div slot="footer">
        <Button @click="modalVisible = false">取消</Button>
        <Button type="primary" :loading="saving" @click="handleSave">保存</Button>
      </div>
    </Modal>

    <!-- 用工调查汇总 Modal -->
    <Modal v-model="jobModalVisible" title="用工调查汇总" width="760" footer-hide>
      <div class="job-summary" v-if="jobSummary">
        <Row type="flex" :gutter="16" class="summary-stat">
          <Col :span="8">
            <div class="stat-card">
              <p class="stat-num">{{ jobSummary.total_job_count }}</p>
              <p class="stat-label">岗位总数</p>
            </div>
          </Col>
          <Col :span="8">
            <div class="stat-card">
              <p class="stat-num">{{ jobSummary.total_headcount }}</p>
              <p class="stat-label">需求总人数</p>
            </div>
          </Col>
          <Col :span="8">
            <div class="stat-card">
              <p class="stat-num">{{ jobSummary.by_job ? jobSummary.by_job.length : 0 }}</p>
              <p class="stat-label">职位种类</p>
            </div>
          </Col>
        </Row>
        <Divider>按职位汇总</Divider>
        <Table :columns="jobColumns" :data="jobSummary.by_job || []" size="small" />
        <Divider>详细填报记录</Divider>
        <Table :columns="recordColumns" :data="jobSummary.records || []" size="small" />
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  getSurveyList,
  getSurveyDetail,
  saveSurvey,
  deleteSurvey,
  publishSurvey,
  getSurveyJobdata
} from '@/api/survey'

export default {
  name: 'survey-manage',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: {
        page: 1,
        limit: 20,
        key: '',
        type: null,
        status: null
      },
      columns: [
        { title: 'ID', key: 'id', width: 60 },
        { title: '标题', key: 'title', ellipsis: true },
        { title: '类型', slot: 'type', width: 100 },
        { title: '状态', slot: 'status', width: 90 },
        { title: '有效期', key: 'validRangeText', width: 220 },
        { title: '已回收', key: 'filled_count', width: 90 },
        { title: '操作', slot: 'action', width: 220, fixed: 'right' }
      ],
      modalVisible: false,
      isEdit: false,
      saving: false,
      form: {
        id: null,
        type: 1,
        title: '',
        target_scope: 'all',
        validRange: [],
        valid_start: '',
        valid_end: '',
        config: []
      },
      rules: {
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }]
      },
      jobModalVisible: false,
      jobSummary: null,
      jobColumns: [
        { title: '职位名称', key: 'job_name' },
        { title: '需求人数', key: 'headcount' }
      ],
      recordColumns: [
        { title: 'ID', key: 'id', width: 70 },
        { title: '职位名称', key: 'job_name' },
        { title: '企业ID', key: 'company_id', width: 100 },
        { title: '需求人数', key: 'headcount', width: 100 }
      ]
    }
  },
  created() {
    this.load()
  },
  methods: {
    statusText(s) {
      return { 0: '草稿', 1: '已发布', 2: '已结束' }[s] || '未知'
    },
    statusColor(s) {
      return { 0: 'default', 1: 'green', 2: 'orange' }[s] || 'default'
    },
    async load() {
      this.loading = true
      try {
        const res = await getSurveyList(this.query.page, this.query.limit, {
          key: this.query.key,
          type: this.query.type,
          status: this.query.status
        })
        this.list = (res.data || []).map(i => ({
          ...i,
          validRangeText: (i.valid_start ? i.valid_start.slice(0, 16) : '—') + ' ~ ' + (i.valid_end ? i.valid_end.slice(0, 16) : '—')
        }))
        this.total = res.count || this.list.length
      } catch (e) {
        this.$Message.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.query.page = 1
      this.load()
    },
    changePage(p) {
      this.query.page = p
      this.load()
    },
    changeSize(s) {
      this.query.limit = s
      this.query.page = 1
      this.load()
    },
    selectionChange() {},
    handleCreate() {
      this.isEdit = false
      this.form = {
        id: null,
        type: 1,
        title: '',
        target_scope: 'all',
        validRange: [],
        valid_start: '',
        valid_end: '',
        config: []
      }
      this.modalVisible = true
    },
    async handleEdit(row) {
      this.isEdit = true
      try {
        const res = await getSurveyDetail(row.id)
        const d = res.data || {}
        this.form = {
          id: d.id,
          type: d.type,
          title: d.title || '',
          target_scope: d.target_scope || 'all',
          validRange: d.valid_start && d.valid_end
            ? [new Date(d.valid_start.replace(/-/g, '/')), new Date(d.valid_end.replace(/-/g, '/'))]
            : [],
          valid_start: d.valid_start,
          valid_end: d.valid_end,
          config: Array.isArray(d.config) ? d.config.map(c => ({ ...c, options: Array.isArray(c.options) ? [...c.options] : [] })) : []
        }
        this.modalVisible = true
      } catch (e) {
        this.$Message.error('加载问卷失败')
      }
    },
    onTypeChange(type) {
      if (type === 2) this.form.config = []
    },
    addQuestion() {
      this.form.config.push({ qtype: 'radio', title: '', options: ['选项1', '选项2'], required: 1 })
    },
    removeQuestion(i) {
      this.form.config.splice(i, 1)
    },
    addOption(q) {
      q.options.push('')
    },
    removeOption(q, i) {
      q.options.splice(i, 1)
    },
    handleSave() {
      this.$refs.surveyForm.validate(async (valid) => {
        if (!valid) return
        if (this.form.type === 1 && !this.form.config.length) {
          this.$Message.warning('请至少添加一道题目')
          return
        }
        if (this.form.type === 1) {
          for (const q of this.form.config) {
            if (!q.title) { this.$Message.warning('存在未填写的题目'); return }
          }
        }
        const v = this.form.validRange || []
        const fmt = (d) => {
          if (!d) return ''
          const p = n => String(n).padStart(2, '0')
          return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
        }
        const payload = {
          id: this.form.id || null,
          type: this.form.type,
          title: this.form.title,
          target_scope: this.form.target_scope,
          valid_start: v && v[0] ? fmt(v[0]) : this.form.valid_start,
          valid_end: v && v[1] ? fmt(v[1]) : this.form.valid_end,
          status: this.isEdit ? undefined : 0,
          config: this.form.config
        }
        this.saving = true
        try {
          const res = await saveSurvey(payload)
          if (res.code === 0) {
            this.$Message.success('保存成功')
            this.modalVisible = false
            this.load()
          } else {
            this.$Message.error(res.msg || '保存失败')
          }
        } catch (e) {
          this.$Message.error('保存失败')
        } finally {
          this.saving = false
        }
      })
    },
    async handlePublish(row) {
      this.$Modal.confirm({
        title: '发布确认',
        content: `确定发布「${row.title}」吗？发布后用户将在消息列表收到系统消息。`,
        onOk: async () => {
          try {
            const res = await publishSurvey(row.id)
            if (res.code === 0) { this.$Message.success('已发布'); this.load() }
            else this.$Message.error(res.msg || '发布失败')
          } catch (e) { this.$Message.error('发布失败') }
        }
      })
    },
    handleDelete(row) {
      this.$Modal.confirm({
        title: '删除确认',
        content: `确定删除「${row.title}」吗？`,
        onOk: async () => {
          try {
            const res = await deleteSurvey(row.id)
            if (res.code === 0) { this.$Message.success('已删除'); this.load() }
            else this.$Message.error(res.msg || '删除失败')
          } catch (e) { this.$Message.error('删除失败') }
        }
      })
    },
    async handleViewJob(row) {
      try {
        const res = await getSurveyJobdata(row.id)
        this.jobSummary = res.data
        this.jobModalVisible = true
      } catch (e) {
        this.$Message.error('加载汇总失败')
      }
    }
  }
}
</script>

<style lang="less" scoped>
.survey-manage {
  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    .ml-8 { margin-left: 8px; }
    .toolbar-right { display: flex; align-items: center; }
  }
  .pager {
    margin-top: 16px;
    text-align: right;
  }
  .question-builder {
    .question-item {
      border: 1px dashed #dcdee2;
      border-radius: 6px;
      padding: 12px;
      margin-bottom: 12px;
      .q-head { display: flex; align-items: center; }
      .q-no { font-weight: 600; color: #2d8cf0; }
      .q-opts { margin-top: 10px; padding-left: 34px; }
      .opt-list { margin-top: 8px; }
      .opt-row { display: flex; align-items: center; margin-bottom: 6px; }
    }
  }
  .job-summary {
    .summary-stat { margin-bottom: 8px; }
    .stat-card {
      background: #f7f8fa;
      border-radius: 6px;
      text-align: center;
      padding: 16px 0;
      .stat-num { font-size: 28px; font-weight: 700; color: #2d8cf0; }
      .stat-label { color: #999; margin-top: 4px; }
    }
  }
}
</style>